import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { Validators, AbstractControl, NonNullableFormBuilder, FormControl } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { StorageServiceService } from "@onsip/common/services/api/resources/storageService/storage-service.service";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { BehaviorSubject, Subscription } from "rxjs";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { StorageService } from "@onsip/common/services/api/resources/storageService/storage-service";
import { IApiError } from "@onsip/common/services/api/onsip-api-action-new";
import { UserService } from "@onsip/common/services/api/resources/user/user.service";

const CASH_EXPRESS_ACCOUNT_ID = "111747";

@Component({
  selector: "onsip-add-storage-service-dialog",
  styleUrls: ["./add-storage-service-dialog.component.scss"],
  templateUrl: "./add-storage-service-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AddStorageServiceDialogComponent implements OnInit, OnDestroy {
  storageInfo = this.fb.group({
    name: ["", Validators.required],
    storageService: [""],
    awsBucketName: [""],
    awsAccessKey: [""],
    awsSecretKey: [""],
    rackspaceApiKey: [""],
    rackspaceUsername: [""],
    rackspaceContainerName: [""],
    gCloudProjectId: [""],
    gCloudClientEmail: [""],
    gCloudPrivateKey: [""],
    gCloudBucketName: [""],
    fileName: [""],
    /** this only for cash express custom url */
    awsCustomUrl: [""]
  });
  /** check if account is cashexpress */
  isCashExpress = false;

  E2eLocators = E2eLocators;

  loadingStateComplete$ = new BehaviorSubject(true);

  private invalidFileFormatError = false;
  private unsubscriber = new Subscription();

  constructor(
    private dialogRef: MatDialogRef<AddStorageServiceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: StorageService,
    //private matDialog: MatDialog,
    private storageService: StorageServiceService,
    private snackbar: SnackbarService,
    private translate: TranslateService,
    private fb: NonNullableFormBuilder,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.checkAccount();
    this.setFormValues();
    this.handleServiceSelect();
    if (this.data) {
      this.populateForm();
    }
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  /** check if current account is cash express */
  private checkAccount() {
    this.unsubscriber.add(
      this.userService.accountState.subscribe(state => {
        const user = state[0];
        this.isCashExpress = user.accountId === CASH_EXPRESS_ACCOUNT_ID;
      })
    );
  }

  private populateForm(): void {
    this.storageInfo.patchValue({
      name: this.data.name,
      storageService: this.data.service,
      awsBucketName: this.data.awsBucketName,
      awsAccessKey: this.data.awsAccessKey,
      awsSecretKey: this.data.awsSecretKey,
      rackspaceApiKey: this.data.rackspaceApiKey,
      rackspaceUsername: this.data.rackspaceUsername,
      rackspaceContainerName: this.data.rackspaceContainerName,
      gCloudProjectId: this.data.gCloudProjectId,
      gCloudClientEmail: this.data.gCloudClientEmail,
      gCloudPrivateKey: this.data.gCloudPrivateKey,
      gCloudBucketName: this.data.gCloudBucketName,
      awsCustomUrl: this.data.awsCustomUrl
    });
  }

  setFormValues(): void {
    this.setAwsValidators();
    this.storageInfo.controls.storageService.setValue("AWS");
  }

  handleServiceSelect(): void {
    this.unsubscriber.add(
      this.storageInfo.controls.storageService.valueChanges.subscribe(value => {
        switch (value) {
          case "AWS": {
            this.clearFormField(this.storageInfo.controls.rackspaceApiKey);
            this.clearFormField(this.storageInfo.controls.rackspaceContainerName);
            this.clearFormField(this.storageInfo.controls.rackspaceUsername);
            this.clearFormField(this.storageInfo.controls.gCloudBucketName);
            this.clearFormField(this.storageInfo.controls.gCloudClientEmail);
            this.clearFormField(this.storageInfo.controls.gCloudPrivateKey);
            this.clearFormField(this.storageInfo.controls.gCloudProjectId);
            this.clearFormField(this.storageInfo.controls.fileName);
            this.clearFormField(this.storageInfo.controls.awsCustomUrl);
            this.setAwsValidators();
            this.storageInfo.updateValueAndValidity();
            break;
          }
          case "Google Cloud": {
            this.clearFormField(this.storageInfo.controls.awsAccessKey);
            this.clearFormField(this.storageInfo.controls.awsBucketName);
            this.clearFormField(this.storageInfo.controls.awsSecretKey);
            this.clearFormField(this.storageInfo.controls.rackspaceApiKey);
            this.clearFormField(this.storageInfo.controls.rackspaceContainerName);
            this.clearFormField(this.storageInfo.controls.rackspaceUsername);
            this.setGoogleCloudValidators();
            this.storageInfo.updateValueAndValidity();
            break;
          }
          case "Rackspace": {
            this.clearFormField(this.storageInfo.controls.awsAccessKey);
            this.clearFormField(this.storageInfo.controls.awsBucketName);
            this.clearFormField(this.storageInfo.controls.awsSecretKey);
            this.clearFormField(this.storageInfo.controls.gCloudBucketName);
            this.clearFormField(this.storageInfo.controls.gCloudClientEmail);
            this.clearFormField(this.storageInfo.controls.gCloudPrivateKey);
            this.clearFormField(this.storageInfo.controls.gCloudProjectId);
            this.clearFormField(this.storageInfo.controls.fileName);
            this.setRackspaceValidators();
            this.storageInfo.updateValueAndValidity();
            break;
          }
          default: {
            break;
          }
        }
      })
    );
  }

  setAwsValidators(): void {
    this.storageInfo.controls.awsBucketName.setValidators([
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(64)
    ]);
    this.storageInfo.controls.awsAccessKey.setValidators([
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(64)
    ]);
    this.storageInfo.controls.awsSecretKey.setValidators([
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(64)
    ]);
    if (this.isCashExpress) {
      this.storageInfo.controls.awsCustomUrl.setValidators([
        Validators.pattern(/^(http|https):\/\//)
      ]);
    }
  }

  setGoogleCloudValidators(): void {
    this.storageInfo.controls.gCloudProjectId.setValidators([
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(100)
    ]);
    this.storageInfo.controls.gCloudClientEmail.setValidators(Validators.required);
    this.storageInfo.controls.gCloudPrivateKey.setValidators([
      Validators.required,
      Validators.minLength(52),
      Validators.maxLength(2500)
    ]);
    this.storageInfo.controls.gCloudBucketName.setValidators([
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(100)
    ]);
    this.storageInfo.controls.fileName.setValidators(Validators.required);
  }

  setRackspaceValidators(): void {
    this.storageInfo.controls.rackspaceApiKey.setValidators([
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(500)
    ]);
    this.storageInfo.controls.rackspaceUsername.setValidators([
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(100)
    ]);
    this.storageInfo.controls.rackspaceContainerName.setValidators([
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(100)
    ]);
  }

  getGenericRequiredError(control: AbstractControl | undefined | null, value: string): string {
    // eslint-disable-next-line no-null/no-null
    if (control === undefined || control === null) return "";
    return control.hasError("required")
      ? this.translate.instant("ONSIP_I18N._VALUE__IS_REQUIRED", { value })
      : "";
  }

  getGenericMinLengthError(control: AbstractControl | undefined | null, value: string): string {
    // eslint-disable-next-line no-null/no-null
    if (control === undefined || control === null) return "";
    return control.hasError("minlength")
      ? this.translate.instant("ONSIP_I18N._VALUE__MUST_BE_MORE_THAN__LENGTH__CHARACTERS", {
          value,
          length: control.errors?.minlength.requiredLength
        })
      : "";
  }

  getGenericMaxLengthError(control: AbstractControl | undefined | null, value: string): string {
    // eslint-disable-next-line no-null/no-null
    if (control === undefined || control === null) return "";
    return control.hasError("maxlength")
      ? this.translate.instant("ONSIP_I18N._VALUE__MUST_BE_LESS_THAN__LENGTH__CHARACTERS", {
          value,
          length: control.errors?.maxlength.requiredLength
        })
      : "";
  }

  getFieldError(control: FormControl<string>, value: string): string {
    return (
      this.getGenericRequiredError(control, value) ||
      this.getGenericMinLengthError(control, value) ||
      this.getGenericMaxLengthError(control, value)
    );
  }

  onFileChange(fileInput: HTMLInputElement): void {
    const uploadedFile = (fileInput as HTMLInputElement & { files: FileList }).files[0];
    if (uploadedFile) {
      // check if file is valid
      if (uploadedFile.type.includes("json")) {
        this.invalidFileFormatError = false;
        this.storageInfo.controls.fileName.setValue(uploadedFile.name);
        this.storageInfo.updateValueAndValidity();
        uploadedFile.text().then(res => {
          const obj = JSON.parse(res);
          if (obj.project_id && obj.private_key && obj.client_email) {
            this.storageInfo.controls.gCloudProjectId.setValue(obj.project_id);
            this.storageInfo.controls.gCloudPrivateKey.setValue(obj.private_key);
            this.storageInfo.controls.gCloudClientEmail.setValue(obj.client_email);
          }
        });
      } else {
        this.invalidFileFormatError = true;
      }
    }
  }

  showSaveSnackbar(): void {
    this.snackbar.openSnackBar(
      this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
      "success"
    );
  }

  cancel(): void {
    this.dialogRef.close();
  }

  handleSave(): void {
    if (this.storageInfo.valid) {
      const params = {
        Service: this.storageInfo.controls.storageService.value,
        Name: this.storageInfo.controls.name.value,
        AwsAccessKey: this.storageInfo.controls.awsAccessKey.value,
        AwsBucketName: this.storageInfo.controls.awsBucketName.value,
        AwsSecretKey: this.storageInfo.controls.awsSecretKey.value,
        GCloudProjectId: this.storageInfo.controls.gCloudProjectId.value,
        GCloudClientEmail: this.storageInfo.controls.gCloudClientEmail.value,
        GCloudPrivateKey: this.storageInfo.controls.gCloudPrivateKey.value,
        GCloudBucketName: this.storageInfo.controls.gCloudBucketName.value,
        RackspaceUsername: this.storageInfo.controls.rackspaceUsername.value,
        RackspaceApiKey: this.storageInfo.controls.rackspaceApiKey.value,
        RackspaceContainerName: this.storageInfo.controls.rackspaceContainerName.value,
        AwsCustomUrl: this.storageInfo.controls.awsCustomUrl.value
      };
      this.loadingStateComplete$.next(false);
      // has saved data so we should do an edit instead
      if (this.data) {
        const editParams = { ...params, ...{ StorageServiceId: this.data.storageServiceId } };
        this.storageService.storageServiceEdit(editParams).then(result => {
          if (result.status === "success") {
            this.showSaveSnackbar();
            this.dialogRef.close(Object.values(result?.data)[0]);
          } else {
            this.snackbar.openSnackBar(this.getErrorMessage(result.data), "error");
            this.storageService.clearErrors();
          }
          this.loadingStateComplete$.next(true);
        });
      } else {
        this.storageService.storageServiceAdd(params).then(result => {
          if (result.status === "success") {
            this.showSaveSnackbar();
            this.dialogRef.close(Object.values(result?.data)[0]);
          } else {
            this.snackbar.openSnackBar(this.getErrorMessage(result.data), "error");
            this.storageService.clearErrors();
          }
          this.loadingStateComplete$.next(true);
        });
      }
    }
  }

  getStorageKeyError(): string {
    const storageValue = this.storageInfo.controls.fileName;
    const gCloudPrivateKey = this.storageInfo.controls.gCloudPrivateKey;
    const gCloudProjectId = this.storageInfo.controls.gCloudProjectId;
    const gCloudClientEmail = this.storageInfo.controls.gCloudClientEmail;
    if (storageValue.touched && storageValue.hasError("required")) {
      return "Key file is required.";
    } else if (this.invalidFileFormatError) {
      return "Key file must be the JSON key file from Google Cloud Storage.";
    } else if (
      (gCloudPrivateKey.touched && !gCloudPrivateKey.value) ||
      (gCloudClientEmail.touched && !gCloudClientEmail.value) ||
      (gCloudProjectId.touched && !gCloudProjectId.value)
    ) {
      return "Key file must be a valid Google Cloud Private Key.";
    } else {
      return "";
    }
  }

  private getErrorMessage(errorData: IApiError): string {
    // check if error is because of invalid google cloud private key from file upload
    const missingJSONKey = ["GCloudClientEmail", "GCloudPrivateKey"];
    if (errorData.parameter && missingJSONKey.includes(errorData.parameter)) {
      return "Key file must be the JSON key file from Google Cloud Storage";
    } else {
      return errorData.message;
    }
  }

  private clearFormField(control: FormControl): void {
    control.setValue("");
    // eslint-disable-next-line no-null/no-null
    control.setErrors(null);
    control.clearValidators();
    control.updateValueAndValidity();
    control.markAsUntouched();
  }
}
